import React from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionSummary, Box, Stack, Typography } from '@mui/material';
import { CircularProgress } from '../../../../../../components';
import { IconChevron } from '../../../../../../assets/icons/icon-chevron';
import { scrollTop } from '../../../../../../utils/helpers';
import { TariffName } from '../../../channel-details/optimizer/configuration/types/tariff-kind';
import { HeatmapLegend } from '../../../common/tariff/components/heatmap-legend';
import { formatHour } from '../../../common/tariff/utils';
import { useDashboardContext } from '../../context/dashboard-context';
import { DashboardPrice } from '../../types/types';
import { getFirstContinuousHours } from '../../utils/eco-box';
import { getLegendParams } from '../../utils/prices';
import { EcoBox } from '../eco-box/eco-box';
import { AnimatedHour, CurrentDayDot } from './hours-by-tariff.styled';

type DashboardTariffHoursProps = {
  prices: DashboardPrice[];
  loading?: boolean;
};

export const DashboardTariffHours: React.FC<DashboardTariffHoursProps> = ({ prices, loading }) => {
  const { t } = useTranslation('dashboard');
  const { tariff, hoursExpanded, toggleHoursExpanded } = useDashboardContext();
  const currentHour = new Date().getHours();

  if (loading) {
    return (
      <Stack alignItems="center" padding={3}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!prices.length) {
    return (
      <Stack alignItems="center" padding="0 24px 24px 24px">
        <Typography>{t('tariff.noData')}</Typography>
      </Stack>
    );
  }

  const handleExpand = () => {
    toggleHoursExpanded();
    if (hoursExpanded) scrollTop();
  };

  const current = prices.find((x) => x.startingHour === currentHour);
  const continuousHours = getFirstContinuousHours(prices, current?.color);
  const legendParams = getLegendParams(tariff as TariffName);

  return (
    <>
      {current && (
        <EcoBox
          color={current.color}
          {...(continuousHours && { subTitle: `${t('tariff.now')} ${continuousHours}` })}
          tariff={tariff}
        />
      )}

      {hoursExpanded && (
        <>
          {legendParams && <HeatmapLegend hideHeader {...(Array.isArray(legendParams) && { ranges: legendParams })} />}
          <Stack alignItems="center" paddingLeft={3} paddingRight={3} marginTop={legendParams ? 0 : 2}>
            <Box
              display="grid"
              gridTemplateColumns={{ xs: 'repeat(4, 1fr)', sm: 'repeat(6, 1fr)' }}
              gap={1}
              width="100%"
              maxWidth={{ xs: 500, sm: 700 }}
            >
              {prices.map((x, i) => {
                const delay = `${(i * 0.025).toFixed(2)}s`;

                return (
                  <AnimatedHour delay={delay} key={x.startingHour} sx={{ backgroundColor: x.color }}>
                    {currentHour === x.startingHour && <CurrentDayDot />}
                    {formatHour(x.startingHour)}
                  </AnimatedHour>
                );
              })}
            </Box>
          </Stack>
        </>
      )}
      <Accordion
        sx={{ padding: '16px 0', backgroundColor: 'var(--color-element)', '&:before': { height: '0px' } }}
        elevation={0}
        disableGutters
        square
        expanded={hoursExpanded}
        onChange={handleExpand}
      >
        <AccordionSummary
          expandIcon={<IconChevron direction="down" className="accent" />}
          sx={{
            '& .MuiAccordionSummary-content': {
              flexGrow: 'unset',
            },
          }}
        >
          <Typography sx={{ color: 'var(--color-accent)', minWidth: 110 }}>
            {t(`tariff.${hoursExpanded ? 'collapse' : 'expand'}`)}
          </Typography>
        </AccordionSummary>
      </Accordion>
    </>
  );
};
